import Task from "../../entities/task";
import {prepareDispatch, prepareRestore, type SaveIntent} from "../../lib/taskLineage";

/**
 * Task actions
 *
 * Call order:
 *
 * TASK_SAVE_QUEUE:	adds the item to the save queue
 * --> TASK_SAVE:	modifies the state
 * --> TASK_SAVE_SUCCESS: if all went well
 * --> TASK_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail TASK_SAVE_QUEUE is during the call to TASK_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at TASK_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: TASK_SAVE_SUCCESS, TASK_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or TASK_PENDING_CHECK (it left the queue).
 */

export const TASK_FETCH_ONE_QUEUE: any = 'TASK_FETCH_ONE_QUEUE';
export const TASK_FETCH_SUCCESS: any = 'TASK_FETCH_SUCCESS';
export const TASK_FETCH_FAILURE: any = 'TASK_FETCH_FAILURE';
export const TASK_FETCH_ONE_SUCCESS: any = 'TASK_FETCH_ONE_SUCCESS';
export const TASK_FETCH_ONE_FAILURE: any = 'TASK_FETCH_ONE_FAILURE';

export const TASK_SAVE: any = 'TASK_SAVE';
export const TASK_SAVE_QUEUE: any = 'TASK_SAVE_QUEUE';
export const TASK_SAVE_SUCCESS: any = 'TASK_SAVE_SUCCESS';
export const TASK_SAVE_FAILURE: any = 'TASK_SAVE_FAILURE';

export const TASK_SAVE_MANY: any = 'TASK_SAVE_MANY';
export const TASK_SAVE_MANY_QUEUE: any = 'TASK_SAVE_MANY_QUEUE';
export const TASK_SAVE_MANY_SUCCESS: any = 'TASK_SAVE_MANY_SUCCESS';
export const TASK_SAVE_MANY_FAILURE: any = 'TASK_SAVE_MANY_FAILURE';

export const TASK_REMOVE: any = 'TASK_REMOVE';
export const TASK_REMOVE_QUEUE: any = 'TASK_REMOVE_QUEUE';
export const TASK_REMOVE_SUCCESS: any = 'TASK_REMOVE_SUCCESS';
export const TASK_REMOVE_FAILURE: any = 'TASK_REMOVE_FAILURE';

export const TASK_RESTORE: any = 'TASK_RESTORE';
export const TASK_RESTORE_QUEUE: any = 'TASK_RESTORE_QUEUE';
export const TASK_RESTORE_SUCCESS: any = 'TASK_RESTORE_SUCCESS';
export const TASK_RESTORE_FAILURE: any = 'TASK_RESTORE_FAILURE';

export const TASK_RESTORE_MANY: any = 'TASK_RESTORE_MANY';
export const TASK_REMOVE_MANY: any = 'TASK_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const TASK_SAVE_REVERT: any = 'TASK_SAVE_REVERT';
export const TASK_PENDING_CHECK: any = 'TASK_PENDING_CHECK';

// OWA-998: a save of this tab was refused as stale; install the record as it is now
export const TASK_STALE: any = 'TASK_STALE';

/* Fetch actions */
export const fetchTaskSuccess = (tasks: Task[], partial: boolean, lastFetch: number) => ({type: TASK_FETCH_SUCCESS, tasks, partial, lastFetch});
export const fetchTaskFailure = (err: any) => {
		console.error('TaskAction: fetchTaskFailure', err);
		return {type: TASK_FETCH_FAILURE, err};
};
export const fetchOneTaskQueue = (taskId: string, sentAddedOn?: number) => ({type: TASK_FETCH_ONE_QUEUE, taskId, sentAddedOn});
export const fetchOneTaskSuccess = (task: Task | null, sentAddedOn?: number, taskId: string = task?.id) => ({type: TASK_FETCH_ONE_SUCCESS, taskId, task, sentAddedOn});
export const fetchOneTaskFailure = (id: string, err: any) => {
		console.error('TaskAction: fetchOneTaskFailure', err);
		return {type: TASK_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveTask = (task: Task) => {
    // console.debug('⮂ TASK_SAVE action', task?.id);
    return {type: TASK_SAVE, task};
}
/**
 * OWA-998: the queued copy carries a new operation id, the save it continues (if any) and the
 * accepted version it began from. The caller's object records the operation, so its next edit
 * continues this save.
 */
export const saveTaskQueue = (task: Task, onQueued?: (saved: boolean) => void, intent?: SaveIntent) => {
    // console.debug('⮂ TASK_SAVE_QUEUE action', task?.id);
    return {type: TASK_SAVE_QUEUE, task: prepareDispatch(task, intent), onQueued};
}
export const saveTaskSuccess = (task: Task, sentAddedOn?: number) => {
    // console.debug('⮂ TASK_SAVE_SUCCESS action', task?.id);
    return {type: TASK_SAVE_SUCCESS, task, sentAddedOn};
}
export const saveTaskFailure = (task: Task, status: number, message: string) => {
    console.warn(`⮂ TASK_SAVE_FAILURE error saving task: HTTP ${status}: ${message}`, task?.id);
    return {type: TASK_SAVE_FAILURE, task, status, message};
};

/* SaveMany actions */
export const saveManyTasks = (tasks: Task[]) => {
    // console.debug('⮂ TASK_SAVE_MANY action', tasks?.map(task => task?.id).join(','));
    return {type: TASK_SAVE_MANY, tasks};
}
/** OWA-998: see saveTaskQueue */
export const saveManyTasksQueue = (tasks: Task[], onQueued?: (saved: boolean) => void, intent?: SaveIntent) => {
    // console.debug('⮂ TASK_SAVE_MANY_QUEUE action', tasks?.map(task => task.id).join(','));
    return {type: TASK_SAVE_MANY_QUEUE, tasks: tasks.map(task => prepareDispatch(task, intent)), onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `tasks` (OWA-1018) */
export const saveManyTasksSuccess = (tasks: Task[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ TASK_SAVE_MANY_SUCCESS action', tasks?.map(task => task.id).join(','));
    return {type: TASK_SAVE_MANY_SUCCESS, tasks, sentAddedOns};
}
export const saveManyTasksFailure = (tasks: Task[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_TASKS_FAILURE Error saving tasks: HTTP ${status}: ${message}`, tasks?.map(task => task.id).join(','));
		return {type: TASK_SAVE_MANY_FAILURE, tasks, status, message};
};

/* Remove actions */
export const removeTask = (task: Task) => ({ type: TASK_REMOVE, task });
export const removeTaskQueue = (task: Task) => ({ type: TASK_REMOVE_QUEUE, task });
export const removeTaskSuccess = (task: Task) => ({ type: TASK_REMOVE_SUCCESS, task });
export const removeTaskFailure = (task: Task, status: number, message: string) => {
	console.error(`Redux: removeTaskFailure Error removing task: HTTP ${status}: ${message}`);
	return ({ type: TASK_REMOVE_FAILURE, task, status, message });
};

/* Restore actions */
export const restoreTask = (task: Task) => ({ type: TASK_RESTORE, task });
export const restoreTaskQueue = (task: Task) => ({ type: TASK_RESTORE_QUEUE, task: prepareRestore(task) });
export const restoreTaskSuccess = (task: Task, sentAddedOn?: number) => ({ type: TASK_RESTORE_SUCCESS, task, sentAddedOn });
export const restoreTaskFailure = (task: Task, status: number, message: string) => {
	console.error(`Redux: restoreTaskFailure Error restoring task: HTTP ${status}: ${message}`);
	return ({ type: TASK_RESTORE_FAILURE, task, status, message });
};

/* RestoreMany actions */
export const restoreManyTasks = (tasks: Task[]) => ({ type: TASK_RESTORE_MANY, tasks });

/* RemoveMany actions */
export const removeManyTasks = (tasks: Task[]) => ({ type: TASK_REMOVE_MANY, tasks });

/* Pending save actions (OWA-1018) */
/**
 * The save `task` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertTaskSave = (task: Task, options: {sentAddedOn?: number, current?: Task | null, keep?: boolean} = {}) =>
    ({type: TASK_SAVE_REVERT, task, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkTaskPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: TASK_PENDING_CHECK, live, refused, readAt});

/**
 * OWA-998: a save of this tab was refused as stale. `current` is the record as it is now (possibly
 * deleted); `rejectedAddedOn` is the refused save's version, which it may replace in the store.
 */
export const staleTask = (current: Task, rejectedAddedOn: number) => ({type: TASK_STALE, task: current, rejectedAddedOn});